import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_site/about')({
  head: () => ({
    meta: [
      { title: 'About Everwake — the engine decides, the AI tells it' },
      {
        name: 'description',
        content:
          'Everwake separates the rules from the storyteller: a deterministic engine owns the world and the dice, and the AI narrates what already happened.',
      },
      { property: 'og:title', content: 'About Everwake' },
      {
        property: 'og:description',
        content:
          'A deterministic engine owns the world, the rules and the dice. The AI game master narrates what the engine decided.',
      },
      { property: 'og:type', content: 'article' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: About,
});

function About() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16">
      <p className="rule-line">About</p>
      <h1 className="mt-4 font-display text-4xl md:text-5xl">
        The engine decides. The AI tells it well.
      </h1>

      <div className="mt-8 space-y-5 text-lg text-muted-foreground">
        <p>
          Everwake is built around a simple promise: your choices should matter, the rules should stay true, and the storyteller should make every outcome feel alive.
        </p>
        <p>
          Everwake splits the two apart. A deterministic engine owns the world, the rules and the dice. The AI game master is handed what happened and narrates it. It can propose a check, a trade or a change of scene, but only through strictly typed requests the engine validates — and the engine upholds the integrity of every roll.
        </p>
        <p>
          That is why the dice can be sealed and verified, why death is allowed to happen, why the battle grid is the room you were already standing in, and why the world you left is the world you come back to.
        </p>
      </div>

      <section className="mt-12 border-t border-border/70 pt-10">
        <h2 className="font-display text-2xl">Where the game is today</h2>
        <p className="mt-4 text-muted-foreground">
          Everwake is currently in controlled playtests. The engine, rules, world, AI layer, content pack, session server, web client and voice are running and tested; the game master has narrated live and voice has been heard in the browser. In September 2026, two campaigns were played end to end in the cloud across 83 turns, and both authored endings were reached. The next stage adds accounts, billing, campaign continuity between sessions and the character builder.
        </p>
      </section>

      <section className="mt-12 border-t border-border/70 pt-10">
        <h2 className="font-display text-2xl">The road ahead</h2>
        <p className="mt-4 text-muted-foreground">
          The team is exploring higher-fidelity voice, private parties shaped around thoughtful safety rules, and new worlds beyond Ashford. Today, the focus remains the single-player, English-language controlled playtest described across this site.
        </p>
      </section>

      <section className="mt-12 border-t border-border/70 pt-10">
        <h2 className="font-display text-2xl">Who makes it</h2>
        <p className="mt-4 text-muted-foreground">
          Everwake is made by a company registered in the Netherlands and sold worldwide. The game is in English.
        </p>
      </section>
    </div>
  );
}
